import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Dialog } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { AutoComplete } from '../../../pda/components/AutoComplete';
import {
    GET_WAREHOUSE_DETAILS_BY_DESTINATION,
    GET_PDA_BAGGING_NEXT_STOP_OPTIONS,
    GetPdaBaggingNextStopOptionsResponse,
    GetPdaBaggingNextStopOptionsVariables,
    GetWarehouseDetailsByDestinationResponse,
    GetWarehouseDetailsByDestinationVariables,
    WarehouseDetailType
} from '../../../../graphql/queries/warehouseQueries';
import { showError } from '../../../../utils/newExceptionUtils';
import { playScanFailedAoSound } from '../../../../utils/scanUtils';

interface DestinationSelectorProps {
    destinations: string[];
    isLocked: boolean;
    onChange: (destinations: string[]) => void;
    onLockToggle: () => void;
    onDestinationInfoChange?: (infoMap: DestinationInfo) => void; // 传递目的地信息映射
    onDestinationRemove?: (destination: string) => void; // 删除目的地时的回调，用于移除匹配的包牌
    /** 可选标签，如「下一站 (不填表示不限)」 */
    label?: string;
}

// 存储目的地详细信息，key 是 sortingCode，value 是 WarehouseDetailType
interface DestinationInfo {
    [sortingCode: string]: WarehouseDetailType;
}

export const DestinationSelector: React.FC<DestinationSelectorProps> = ({
    destinations,
    isLocked,
    onChange,
    onLockToggle,
    onDestinationInfoChange,
    onDestinationRemove,
    label,
}) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<Array<{ label: string; value: string }>>([]);
    const [destinationInfoMap, setDestinationInfoMap] = useState<DestinationInfo>({});

    // 判断是否为 TT 目的地（code 形如 "LAX1 (tt)"，不需要走 SWX 站点详情查询）
    const isTtDestination = (dest: string): boolean => dest.trim().toLowerCase().endsWith('(tt)');

    // 当destinations变化时，如果destinationInfoMap中没有对应的信息，需要查询（仅 SWX）
    useEffect(() => {
        if (destinations.length > 0) {
            // 检查哪些目的地没有信息（仅针对 SWX，TT 不需要补充详情）
            const missingDestinations = destinations.filter(dest => !isTtDestination(dest) && !destinationInfoMap[dest]);
            if (missingDestinations.length > 0) {
                // 使用第一个缺失目的地的前缀进行查询
                const firstMissing = missingDestinations[0];
                if (firstMissing && firstMissing.length > 0) {
                    const queryPrefix = firstMissing.substring(0, Math.min(3, firstMissing.length));
                    getWarehouseDetailsByDestination({
                        variables: { destination: queryPrefix }
                    });
                }
            }
        }
    }, [destinations]);

    const [getWarehouseDetailsByDestination] = useLazyQuery<
        GetWarehouseDetailsByDestinationResponse,
        GetWarehouseDetailsByDestinationVariables
    >(GET_WAREHOUSE_DETAILS_BY_DESTINATION, {
        onCompleted: (data) => {
            const warehouses = data.getWarehouseDetailsByDestination || [];

            if (warehouses.length === 0) {
                setOptions([]);
                return;
            }

            // 构建目的地信息映射
            // 同时使用sortingCode和name作为key，因为destinations数组可能存储的是sortingCode或name
            const infoMap: DestinationInfo = {};
            warehouses.forEach(warehouse => {
                if (warehouse.sortingCode) {
                    infoMap[warehouse.sortingCode] = warehouse;
                }
                // 如果name和sortingCode不同，也用name作为key
                if (warehouse.name && warehouse.name !== warehouse.sortingCode) {
                    infoMap[warehouse.name] = warehouse;
                }
            });
            setDestinationInfoMap(prev => {
                const newMap = { ...prev, ...infoMap };
                // 通知父组件目的地信息变化
                onDestinationInfoChange?.(newMap);
                return newMap;
            });
            
            // 如果当前已选择的目的地中有新查询到的，也需要更新映射
            destinations.forEach(dest => {
                const warehouse = warehouses.find(w => 
                    (w.sortingCode && w.sortingCode === dest) || 
                    (w.name && w.name === dest)
                );
                if (warehouse && !infoMap[dest]) {
                    // 如果找到了匹配的warehouse但还没有在infoMap中，添加到映射
                    if (warehouse.sortingCode) {
                        infoMap[warehouse.sortingCode] = warehouse;
                    }
                    if (warehouse.name && warehouse.name !== warehouse.sortingCode) {
                        infoMap[warehouse.name] = warehouse;
                    }
                }
            });
            
            // 再次更新映射，确保包含所有已选择的目的地
            if (Object.keys(infoMap).length > 0) {
                setDestinationInfoMap(prev => {
                    const finalMap = { ...prev, ...infoMap };
                    onDestinationInfoChange?.(finalMap);
                    return finalMap;
                });
            }

            // 过滤选项：排除不符合选择规则的选项
            const filteredWarehouses = filterOptions(warehouses, destinations, infoMap);

            const optionsList = filteredWarehouses.map(item => ({
                label: item.sortingCode || item.name,
                value: item.sortingCode || item.name
            }));
            setOptions(optionsList);
        },
        onError: (error) => {
            console.error('Failed to fetch warehouse destinations:', error);
            setOptions([]);
        }
    });

    const [getPdaNextStopOptions] = useLazyQuery<
        GetPdaBaggingNextStopOptionsResponse,
        GetPdaBaggingNextStopOptionsVariables
    >(GET_PDA_BAGGING_NEXT_STOP_OPTIONS, {
        onCompleted: (data) => {
            const list = data.getPdaBaggingNextStopOptions || [];
            if (list.length === 0) {
                setOptions([]);
                return;
            }
            const opts = list.map(item => {
                const isTt = item.source === 'TT';
                const label = isTt ? `${item.code} (tt)` : item.code;
                const value = label; // 选中后也展示 code(tt)，且避免二行展示
                return {
                    label,
                    value,
                };
            });
            setOptions(opts);
        },
        onError: (error) => {
            console.error('Failed to fetch PDA bagging next stop options:', error);
            setOptions([]);
        }
    });

    // 当destinations变化时，如果destinationInfoMap中没有对应的信息，需要查询（仅 SWX）
    useEffect(() => {
        if (destinations.length > 0) {
            // 检查哪些目的地没有信息（仅针对 SWX，TT 不需要补充详情）
            const missingDestinations = destinations.filter(dest => !isTtDestination(dest) && !destinationInfoMap[dest]);
            if (missingDestinations.length > 0) {
                // 使用第一个缺失目的地的前缀进行查询
                const firstMissing = missingDestinations[0];
                if (firstMissing && firstMissing.length > 0) {
                    const queryPrefix = firstMissing.substring(0, Math.min(3, firstMissing.length));
                    getWarehouseDetailsByDestination({
                        variables: { destination: queryPrefix }
                    });
                }
            }
        }
    }, [destinations, destinationInfoMap, getWarehouseDetailsByDestination]);

    // 判断是否是Hub（siteType === 1 表示 Hub，siteType === 2 表示 Warehouse）
    const isHub = (warehouse: WarehouseDetailType): boolean => {
        return warehouse.siteType === 1;
    };

    // 判断warehouse是否属于某个hub（warehouse的siteType必须是2，且parentSiteId等于hubId）
    const isWarehouseBelongsToHub = (warehouse: WarehouseDetailType, hubId: string): boolean => {
        return warehouse.siteType === 2 && warehouse.parentSiteId === hubId;
    };

    // 过滤选项：排除已选中hub下的warehouse和已选中hub
    const filterOptions = (warehouses: WarehouseDetailType[], selectedDestinations: string[], infoMap: DestinationInfo): WarehouseDetailType[] => {
        return warehouses.filter(warehouse => {
            const sortingCode = warehouse.sortingCode || warehouse.name;
            
            // 如果是hub，检查是否已有该hub下的warehouse被选中
            if (isHub(warehouse)) {
                const hasWarehouseFromThisHub = selectedDestinations.some(dest => {
                    const destInfo = infoMap[dest];
                    return destInfo && isWarehouseBelongsToHub(destInfo, warehouse.id);
                });
                return !hasWarehouseFromThisHub;
            } else {
                // 如果是warehouse，检查是否已有该warehouse所属的hub被选中
                const hubId = warehouse.parentSiteId;
                const hubSelected = selectedDestinations.some(dest => {
                    const destInfo = infoMap[dest];
                    return destInfo && isHub(destInfo) && destInfo.id === hubId;
                });
                return !hubSelected;
            }
        });
    };

    // 检查是否可以添加该目的地
    const canAddDestination = (newDest: string): boolean => {
        const newDestInfo = destinationInfoMap[newDest];
        if (!newDestInfo) {
            return true; // 如果没有信息，允许添加（兼容旧数据）
        }

        // 如果新选择的是hub
        if (isHub(newDestInfo)) {
            // 检查是否已有该hub下的warehouse被选中
            const hasWarehouseFromThisHub = destinations.some(dest => {
                const destInfo = destinationInfoMap[dest];
                return destInfo && isWarehouseBelongsToHub(destInfo, newDestInfo.id);
            });
            return !hasWarehouseFromThisHub;
        } else {
            // 如果新选择的是warehouse
            // 检查是否已有该warehouse所属的hub被选中
            const hubId = newDestInfo.parentSiteId;
            const hubSelected = destinations.some(dest => {
                const destInfo = destinationInfoMap[dest];
                return destInfo && isHub(destInfo) && destInfo.id === hubId;
            });
            return !hubSelected;
        }
    };

    const handleInputChange = (value: string) => {
        setInputValue(value);

        if (value.trim() && !isLocked) {
            // 新页面：下一站下拉使用后端合并的 SWX + TT 数据源
            getPdaNextStopOptions({
                variables: { destination: value.trim() }
            });
        } else {
            setOptions([]);
        }
    };

    const handleOptionSelect = (value: string) => {
        const trimmedValue = value.trim();
        if (trimmedValue) {
            if (destinations.includes(trimmedValue)) {
                // 如果已选中，则取消选中
                onChange(destinations.filter(d => d !== trimmedValue));
            } else {
                // 检查是否可以添加
                if (!canAddDestination(trimmedValue)) {
                    // 不能添加，显示错误提示
                    const newDestInfo = destinationInfoMap[trimmedValue];
                    playScanFailedAoSound();
                    showError({
                        title: t('pda.common.error'),
                        message: newDestInfo && isHub(newDestInfo)
                            ? t('pda.pages.bagging.cannotSelectHubWhenWarehouseSelected')
                            : t('pda.pages.bagging.cannotSelectWarehouseWhenHubSelected')
                    });
                    return;
                }
                // 如果未选中且可以添加，则添加
                onChange([...destinations, trimmedValue]);
            }
            setInputValue('');
            setOptions([]);
        }
    };

    const handleRemoveDestination = (index: number) => {
        if (!isLocked) {
            const destinationToRemove = destinations[index];
            if (!destinationToRemove) {
                return;
            }

            // 显示确认对话框
            Dialog.confirm({
                title: t('pda.pages.bagging.deleteDestinationConfirm.title'),
                content: (
                    <div className="text-center">
                        <div className="text-base mb-2">{t('pda.pages.bagging.deleteDestinationConfirm.message')}</div>
                        <div className="text-lg font-semibold">{destinationToRemove}</div>
                    </div>
                ),
                cancelText: t('pda.common.cancel'),
                confirmText: t('pda.common.confirm'),
                onConfirm: () => {
                    // 确定后，删除目的地
                    const newDestinations = destinations.filter((_, i) => i !== index);
                    onChange(newDestinations);
                    // 通知父组件移除匹配的包牌
                    onDestinationRemove?.(destinationToRemove);
                }
            });
        }
    };

    return (
        <div className="w-full">
            <div className="flex items-center gap-2 mb-2">
                {!isLocked && (
                    <div className="flex-1">
                        <AutoComplete
                            id="bagging-scan-nextstop-input"
                            name="bagging-scan-nextstop"
                            className="w-full p-2 border rounded"
                            placeholder={label || t('pda.pages.sorting.destination') || undefined}
                            value={inputValue}
                            onChange={handleInputChange}
                            onSelect={handleOptionSelect}
                            options={options}
                            disabled={isLocked}
                            selectedValues={destinations}
                        />
                    </div>
                )}
                <button
                    onClick={onLockToggle}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100"
                >
                    {isLocked ? (
                        <LockOutlined className="text-blue-500 text-lg" />
                    ) : (
                        <UnlockOutlined className="text-gray-400 text-lg" />
                    )}
                </button>
            </div>

            {destinations.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {destinations.map((dest, index) => (
                        <div
                            key={index}
                            className={`px-3 py-1 rounded-md text-sm flex items-center gap-1 ${
                                isLocked
                                    ? 'bg-gray-200 text-gray-600'
                                    : 'bg-blue-100 text-blue-800'
                            }`}
                        >
                            <span>{dest}</span>
                            {!isLocked && (
                                <button
                                    onClick={() => handleRemoveDestination(index)}
                                    className="text-blue-600 hover:text-blue-800 font-bold ml-1 text-base leading-none"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
